import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { tg } from "../lib/telegram";

type Tab = { path: string; labelKey: string; icon: React.FC<{ active: boolean }> };

const CalendarIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.4 : 2}>
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M3 10h18" />
    <path d="M8 3v4M16 3v4" strokeLinecap="round" />
  </svg>
);

const UserIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.4 : 2}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
  </svg>
);

const TABS: Tab[] = [
  { path: "/events", labelKey: "tabBar.events", icon: CalendarIcon },
  { path: "/profile", labelKey: "tabBar.profile", icon: UserIcon },
];

export function TabBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setSheetOpen(true);
    const onClose = () => setSheetOpen(false);
    window.addEventListener("sheet:open", onOpen);
    window.addEventListener("sheet:close", onClose);
    return () => {
      window.removeEventListener("sheet:open", onOpen);
      window.removeEventListener("sheet:close", onClose);
    };
  }, []);

  function go(path: string) {
    if (location.pathname === path) return;
    tg()?.HapticFeedback?.impactOccurred("light");
    navigate(path);
  }

  if (sheetOpen) return null;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
      }}
    >
      <div
        className="
          flex items-center gap-1 px-2 py-2
          bg-surface border border-ink-100
          rounded-full shadow-[var(--shadow-elevated)]
          backdrop-blur-md
        "
      >
        {TABS.map((tab) => {
          const active = location.pathname === tab.path;
          const Icon = tab.icon;
          return (
            <button
              key={tab.path}
              type="button"
              onClick={() => go(tab.path)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-full
                font-semibold text-[14px]
                transition-all duration-200
                ${active
                  ? "bg-brand-purple text-white shadow-[var(--shadow-card)]"
                  : "text-ink-500 hover:text-ink-900 hover:bg-ink-50"
                }
              `}
            >
              <Icon active={active} />
              {active && <span>{t(tab.labelKey)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
